import { useState, useEffect } from "react";

export const monthName = (num) =>
  [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][num - 1] || "—";

export const isExtinguisher = (name) =>
  name && name.toLowerCase().includes("extinguisher");

// Converts a stored UTC ISO string into what the browser's own local wall
// clock reads, for seeding a <input type="datetime-local"> — uses Date's
// local-time getters (not the UTC variants) so this is a real timezone
// conversion, not a naive string slice.
export function isoToLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Reverse of isoToLocalInputValue — a bare datetime-local string (no
// timezone offset) is parsed by `new Date(...)` as local time automatically,
// so toISOString() correctly converts it back to true UTC for the backend.
export function localInputValueToIso(value) {
  return value ? new Date(value).toISOString() : null;
}

// Natural sort comparator for unit numbers ("2" before "10", not after) —
// matches the backend's natural_sort_key in SiteUnitViewSet so combined
// tenant/unit reports order the same way the Units list does.
export function naturalCompare(a, b) {
  const ax = String(a ?? "").match(/\d+|\D+/g) || [];
  const bx = String(b ?? "").match(/\d+|\D+/g) || [];
  const len = Math.max(ax.length, bx.length);
  for (let i = 0; i < len; i++) {
    const av = ax[i] ?? "";
    const bv = bx[i] ?? "";
    if (av === bv) continue;
    const an = /^\d+$/.test(av), bn = /^\d+$/.test(bv);
    if (an && bn) {
      const diff = parseInt(av, 10) - parseInt(bv, 10);
      if (diff !== 0) return diff;
    } else {
      const cmp = av.toLowerCase().localeCompare(bv.toLowerCase());
      if (cmp !== 0) return cmp;
    }
  }
  return 0;
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// Downscale and re-encode to JPEG before upload.
//
// Two problems this solves. Library photos arrive at full camera resolution
// (4-12MB is normal), where a live capture is comparatively small — nothing
// else in the app resizes before upload. And iOS library photos are commonly
// HEIC, which Pillow's ImageField validation rejects outright; Safari decodes
// HEIC natively, so the canvas round-trip normalises it to JPEG on the way out.
//
// Falls back to the original file on any failure — a decode problem must never
// block a tech from attaching evidence.
export async function prepareImageForUpload(file, maxDim = 2000, quality = 0.85) {
  if (!file || !file.type?.startsWith("image/")) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    // Already small enough and already a JPEG — nothing to gain.
    if (scale === 1 && file.type === "image/jpeg") return file;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    if (!blob) return file;

    const name = file.name.replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Archived records drop out of the main lists and out of the contract value.
// A site counts as archived when its own status says so, or when the customer
// it belongs to has been archived — losing a customer retires their sites.
export const isArchivedAccount = (account) =>
  !!account && account.status !== "active";

export const isArchivedSite = (site, account) => {
  if (!site) return false;
  if (site.status !== "active") return true;
  const accountStatus = site.account_status ?? account?.status;
  return accountStatus !== undefined && accountStatus !== "active";
};
